<div class="queue-form">
  <!-- Breadcrumb Header -->
  <div class="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-4">
    <div class="queue-form__breadcrumb small">
      <a class="queue-form__back-link text-decoration-none" (click)="onCancel()">
        {{ 'Settings::Queues:Title' | abpLocalization }}
      </a>
      <span class="queue-form__breadcrumb-sep mx-2">/</span>
      <strong>{{ isEditMode ? ('Settings::Queues:EditQueue' | abpLocalization) : ('Settings::Queues:NewQueue' | abpLocalization) }}</strong>
    </div>
  </div>

  <form [formGroup]="form" class="d-flex flex-column">
    <!-- Queue Name -->
    <div class="mb-2">
      <label class="form-label fw-semibold mb-0 indicates-required-field">
        {{ 'Settings::Queues:NameLabel' | abpLocalization }}
      </label>
      <input
        type="text"
        pInputText
        formControlName="name"
        class="form-control queue-form__name-input w-100 py-2 px-0"
        [placeholder]="'Settings::Queues:NamePlaceholder' | abpLocalization" />
    </div>

    <!-- Queue Description -->
    <div class="mb-3">
      <textarea
        pTextarea
        formControlName="description"
        rows="2"
        class="queue-form__description-input w-100 px-0"
        [placeholder]="'Settings::Queues:DescriptionPlaceholder' | abpLocalization">
      </textarea>
    </div>

    <!-- Active toggle -->
    <div class="d-flex align-items-center gap-2 mb-2">
      <p-toggleSwitch formControlName="isActive" />
      <span class="queue-form__toggle-label small">
        {{ (form.get('isActive')?.value ? 'Settings::Queues:ActiveAccepting' : 'Settings::Queues:InactiveNotAccepting') | abpLocalization }}
      </span>
    </div>

    <hr class="queue-form__divider" />

    <!-- Queue Code -->
    <div class="queue-form__section d-grid">
      <div class="pt-1">
        <h5 class="queue-form__section-title ps-2 fs-6 fw-semibold mb-1">{{ 'Settings::Queues:CodeLabel' | abpLocalization }}</h5>
        <p class="queue-form__section-desc small mb-0">{{ 'Settings::Queues:CodeDesc' | abpLocalization }}</p>
      </div>
      <div class="queue-form__section-right">
        <input
          type="text"
          pInputText
          formControlName="code"
          class="w-100"
          [placeholder]="'Settings::Queues:CodePlaceholder' | abpLocalization" />
      </div>
    </div>

    <hr class="queue-form__divider" />

    <!-- Assigned Agents -->
    <div class="queue-form__section d-grid">
      <div class="pt-1">
        <h5 class="queue-form__section-title ps-2 fs-6 fw-semibold mb-1">{{ 'Settings::Queues:AssignedAgents' | abpLocalization }}</h5>
        <p class="queue-form__section-desc small mb-0">{{ 'Settings::Queues:AssignedAgentsDesc' | abpLocalization }}</p>
      </div>
      <div class="queue-form__section-right">
        <cx-multi-select
          [(ngModel)]="selectedAgentIds"
          [ngModelOptions]="{ standalone: true }"
          [options]="availableAgents"
          optionLabel="userName"
          optionValue="id"
          [filter]="true"
          filterBy="name,userName"
          [loading]="loadingAgents"
          [invalid]="isAgentSelectionInvalid"
          [placeholder]="'Settings::Queues:SelectUserPlaceholder' | abpLocalization"
          scrollHeight="240px"
          [itemTemplate]="agentOptionTemplate"
          [selectedItemsTemplate]="selectedAgentsTemplate" />
        <ng-template #agentOptionTemplate let-agent>
          <span class="d-inline-flex align-items-center gap-2 min-w-0">
            <cx-user-avatar
              [userId]="agent.id"
              [name]="agent.name || agent.userName"
              [size]="20"></cx-user-avatar>
            <span class="text-truncate">{{ getAgentDisplayName(agent) }}</span>
          </span>
        </ng-template>
        <ng-template #selectedAgentsTemplate let-removeChip="removeChip">
          <div class="queue-form__agent-chips d-flex flex-wrap align-items-center gap-1">
            @for (agent of visibleSelectedAgents; track agent.id) {
              <p-chip
                [label]="getAgentDisplayName(agent)"
                [removable]="true"
                (onRemove)="removeChip(agent.id, $event)"
                styleClass="queue-form__agent-chip" />
            }
            @if (hiddenSelectedAgents.length) {
              <p-chip
                [label]="'+' + hiddenSelectedAgents.length"
                [pTooltip]="hiddenSelectedAgentsTooltip"
                tooltipPosition="top"
                appendTo="body"
                styleClass="queue-form__agent-chip" />
            }
          </div>
        </ng-template>
        @if (isAgentSelectionInvalid) {
          <small class="text-danger d-block mt-1">
            {{ 'Settings::Queues:AgentsRequired' | abpLocalization }}
          </small>
        }
      </div>
    </div>

    <hr class="queue-form__divider" />

    <!-- Agent Routing Method -->
    <div class="queue-form__section d-grid">
      <div class="pt-1">
        <h5 class="queue-form__section-title ps-2 fs-6 fw-semibold mb-1">{{ 'Settings::Queues:RoutingMethod:Title' | abpLocalization }}</h5>
        <p class="queue-form__section-desc small mb-0">{{ 'Settings::Queues:RoutingMethod:Desc' | abpLocalization }}</p>
      </div>
      <div class="queue-form__section-right">
        <div class="queue-form__option-card queue-form__option-card--selected d-flex align-items-start py-3 px-3">
          <div class="queue-form__option-radio">
            <input type="radio" checked disabled />
          </div>
          <div class="flex-grow-1">
            <div class="fw-semibold fs-5 text-primary">
              <i class="pi pi-clock pi-fw me-1"></i>
              {{ 'Settings::Queues:RoutingMethod:LongestIdleTime:Title' | abpLocalization }}
            </div>
            <p class="queue-form__option-desc small mt-1 text-primary">{{ 'Settings::Queues:RoutingMethod:LongestIdleTime:Desc' | abpLocalization }}</p>
          </div>
        </div>
      </div>
    </div>

    <hr class="queue-form__divider" />

    <!-- Call Priority -->
    <div class="queue-form__section d-grid">
      <div class="pt-1">
        <h5 class="queue-form__section-title ps-2 fs-6 fw-semibold mb-1">{{ 'Settings::Queues:CallPriority:Title' | abpLocalization }}</h5>
        <p class="queue-form__section-desc small mb-0">{{ 'Settings::Queues:CallPriority:Desc' | abpLocalization }}</p>
      </div>
      <div class="queue-form__section-right">
        <div class="queue-form__option-card queue-form__option-card--selected d-flex align-items-start py-3 px-3">
          <div class="queue-form__option-radio">
            <input type="radio" checked disabled />
          </div>
          <div class="flex-grow-1">
            <div class="fw-semibold fs-5 text-primary">{{ 'Settings::Queues:CallPriority:Fifo:Title' | abpLocalization }}</div>
            <p class="queue-form__option-desc small mt-1 text-primary">{{ 'Settings::Queues:CallPriority:Fifo:Desc' | abpLocalization }}</p>
          </div>
        </div>
      </div>
    </div>

    <hr class="queue-form__divider" />

    <!-- Queue Priority Level -->
    <div class="queue-form__section d-grid">
      <div class="pt-1">
        <h5 class="queue-form__section-title ps-2 fs-6 fw-semibold mb-1">{{ 'Queue::QueuePriority:Title' | abpLocalization }}</h5>
        <p class="queue-form__section-desc small mb-0">{{ 'Queue::QueuePriority:Description' | abpLocalization }}</p>
      </div>
      <div class="queue-form__section-right">
        <p-selectButton
          formControlName="priority"
          [options]="queuePriorityOptions"
          optionValue="value"
          [allowEmpty]="false"
          styleClass="w-200"
          class="text-muted"
          >
          <ng-template let-opt pTemplate="item">{{ opt.label | abpLocalization }}</ng-template>
        </p-selectButton>
        <small class="text-muted d-block mt-2">{{ priorityDetailsKey | abpLocalization }}</small>
      </div>
    </div>

    <hr class="queue-form__divider" />

    <!-- Wrap-up Codes -->
    <div class="queue-form__section d-grid">
      <div class="pt-1">
        <h5 class="queue-form__section-title ps-2 fs-6 fw-semibold mb-1">{{ 'Settings::WorkEnvironment:WrapupCodes:Title' | abpLocalization }}</h5>
        <p class="queue-form__section-desc small mb-0">{{ 'Settings::WorkEnvironment:WrapupCodes:Subtitle' | abpLocalization }}</p>
      </div>
      <div class="queue-form__section-right">
        <cx-multi-select
          formControlName="wrapUpCodeIds"
          [options]="availableWrapUpCodes"
          optionLabel="name"
          optionValue="id"
          [loading]="loadingWrapUpCodes"
          [maxSelectedLabels]="0"
          [placeholder]="'Queue::SelectWrapUpCodePlaceholder' | abpLocalization" />
      </div>
    </div>

    <hr class="queue-form__divider" />

    <!-- Decline Reasons -->
    <div class="queue-form__section d-grid">
      <div class="pt-1">
        <h5 class="queue-form__section-title ps-2 fs-6 fw-semibold mb-1">{{ 'Queue::DeclineReasons' | abpLocalization }}</h5>
        <p class="queue-form__section-desc small mb-0">{{ 'Queue::DeclineReasonsDesc' | abpLocalization }}</p>
      </div>
      <div class="queue-form__section-right">
        <cx-multi-select
          [(ngModel)]="selectedDeclineReasonIds"
          [ngModelOptions]="{ standalone: true }"
          [options]="availableDeclineReasons"
          optionLabel="name"
          optionValue="id"
          [placeholder]="'Queue::SelectDeclineReasonsPlaceholder' | abpLocalization"
          [filter]="true" />
      </div>
    </div>

    <hr class="queue-form__divider" />

    <!-- Max Response Time -->
    <div class="queue-form__section d-grid">
      <div class="pt-1">
        <h5 class="queue-form__section-title ps-2 fs-6 fw-semibold mb-1">{{ 'Settings::Queues:MaxResponseTime:Title' | abpLocalization }}</h5>
        <p class="queue-form__section-desc small mb-0">{{ 'Settings::Queues:MaxResponseTime:Subtitle' | abpLocalization }}</p>
      </div>
      <div class="queue-form__section-right">
        <div class="d-flex align-items-start gap-2">
          <div class="queue-form__maxresponse-field">
            <input
              type="number"
              pInputText
              formControlName="maxResponseTime"
              min="10"
              max="120"
              class="w-100"/>
          </div>
          <span class="queue-form__input-suffix small fw-medium text-muted pt-2">{{ 'Settings::Queues:MaxResponseTime:Unit' | abpLocalization }}</span>
        </div>
      </div>
    </div>

    <hr class="queue-form__divider" />

    <!-- SLA Target -->
    <div class="queue-form__section d-grid">
      <div class="pt-1">
        <h5 class="queue-form__section-title ps-2 fs-6 fw-semibold mb-1">{{ 'Settings::Queues:SlaTarget:Title' | abpLocalization }}</h5>
        <p class="queue-form__section-desc small mb-0">{{ 'Settings::Queues:SlaTarget:Subtitle' | abpLocalization }}</p>
      </div>
      <div class="queue-form__section-right">
        <div class="d-flex align-items-start gap-2">
          <div class="queue-form__maxresponse-field">
            <input
              type="number"
              pInputText
              formControlName="slaTargetSeconds"
              [blueprints]="slaTargetValidationBlueprints"
              [min]="slaTargetMin"
              step="1"
              class="w-100" />
          </div>
          <span class="queue-form__input-suffix small fw-medium text-muted pt-2">{{ 'Settings::Queues:SlaTarget:Unit' | abpLocalization }}</span>
        </div>
      </div>
    </div>

    <hr class="queue-form__divider" />

    <!-- Reschedule Ownership -->
    <div class="queue-form__section d-grid">
      <div class="pt-1">
        <h5 class="queue-form__section-title ps-2 fs-6 fw-semibold mb-1">{{ 'Settings::Queues:RescheduleOwnership:Title' | abpLocalization }}</h5>
        <p class="queue-form__section-desc small mb-0">{{ 'Settings::Queues:RescheduleOwnership:Subtitle' | abpLocalization }}</p>
      </div>
      <div class="queue-form__section-right d-flex flex-column gap-2">
        <label
          class="queue-form__option-card d-flex align-items-start py-3 px-3 mb-0"
          [class.queue-form__option-card--selected]="!isLastAgentOwnership">
          <div class="queue-form__option-radio">
            <input
              type="radio"
              formControlName="scheduledCallOwnership"
              [value]="ScheduledCallOwnership.Queue" />
          </div>
          <div class="flex-grow-1 fw-semibold">{{ 'Settings::Queues:RescheduleOwnership:Queue' | abpLocalization }}</div>
        </label>

        <label
          class="queue-form__option-card d-flex align-items-start py-3 px-3 mb-0"
          [class.queue-form__option-card--selected]="isLastAgentOwnership">
          <div class="queue-form__option-radio">
            <input
              type="radio"
              formControlName="scheduledCallOwnership"
              [value]="ScheduledCallOwnership.LastAgent" />
          </div>
          <div class="flex-grow-1 fw-semibold">{{ 'Settings::Queues:RescheduleOwnership:LastAgent' | abpLocalization }}</div>
        </label>

        @if (isLastAgentOwnership) {
          <p class="queue-form__option-desc small text-muted mb-0">
            {{ 'Settings::Queues:RescheduleOwnership:LastAgentHint' | abpLocalization }}
          </p>

          <div class="queue-form__announce-nested px-3 py-3">
            <div class="d-flex align-items-center justify-content-between gap-3">
              <label class="form-label mb-0">{{ 'Settings::Queues:LastAgentDuration:Title' | abpLocalization }}</label>
              <div class="d-flex align-items-center gap-2">
                <input
                  type="number"
                  pInputText
                  formControlName="lastAgentDurationMinutes"
                  [skipValidation]="true"
                  [min]="lastAgentDurationMin"
                  [max]="lastAgentDurationMax"
                  step="1"
                  class="queue-form__announce-field" />
                <span class="queue-form__input-suffix small fw-medium text-muted">
                  {{ 'Settings::Queues:LastAgentDuration:Unit' | abpLocalization }}
                </span>
              </div>
            </div>
            @if (
              form.get('lastAgentDurationMinutes')?.invalid &&
              (form.get('lastAgentDurationMinutes')?.dirty || form.get('lastAgentDurationMinutes')?.touched)
            ) {
              <div class="text-danger small text-end mt-2">
                {{ 'Settings::Queues:LastAgentDuration:ValidationError' | abpLocalization }}
              </div>
            }
          </div>
        }
      </div>
    </div>

    <hr class="queue-form__divider" />

    <!-- Hold Music -->
    <div class="queue-form__section d-grid">
      <div class="pt-1">
        <h5 class="queue-form__section-title ps-2 fs-6 fw-semibold mb-1">{{ 'Settings::Queues:HoldMusic:Title' | abpLocalization }}</h5>
        <p class="queue-form__section-desc small mb-0">{{ 'Settings::Queues:HoldMusic:Subtitle' | abpLocalization }}</p>
      </div>
      <div class="queue-form__section-right d-flex flex-column gap-2">
        <label
          class="queue-form__option-card d-flex align-items-start py-3 px-3 mb-0"
          [class.queue-form__option-card--selected]="form.get('holdMusicType')?.value === HoldMusicType.Default">
          <div class="queue-form__option-radio">
            <input
              type="radio"
              formControlName="holdMusicType"
              [value]="HoldMusicType.Default"
              (change)="onHoldMusicTypeChange()" />
          </div>
          <div class="flex-grow-1">
            <div class="fw-semibold">{{ 'Settings::Queues:HoldMusic:Default:Title' | abpLocalization }}</div>
            <p class="queue-form__option-desc small mt-1 mb-0 text-muted">{{ 'Settings::Queues:HoldMusic:Default:Desc' | abpLocalization }}</p>
          </div>
        </label>

        <label
          class="queue-form__option-card d-flex align-items-start py-3 px-3 mb-0"
          [class.queue-form__option-card--selected]="isCustomHoldMusic">
          <div class="queue-form__option-radio">
            <input
              type="radio"
              formControlName="holdMusicType"
              [value]="HoldMusicType.Custom"
              (change)="onHoldMusicTypeChange()" />
          </div>
          <div class="flex-grow-1">
            <div class="fw-semibold">{{ 'Settings::Queues:HoldMusic:Custom:Title' | abpLocalization }}</div>
            <p class="queue-form__option-desc small mt-1 mb-0 text-muted">{{ 'Settings::Queues:HoldMusic:Custom:Desc' | abpLocalization }}</p>
          </div>
        </label>

        @if (isCustomHoldMusic) {
          <div class="queue-form__holdmusic-upload d-flex align-items-center flex-wrap gap-2">
            <input
              #holdMusicInput
              type="file"
              class="d-none"
              [accept]="holdMusicAccept"
              (change)="onHoldMusicFileSelected($event)" />
            <button
              type="button"
              class="btn btn-outline-primary btn-sm"
              [disabled]="isSubmitting"
              (click)="holdMusicInput.click()">
              <i class="pi pi-upload me-1"></i>
              {{ 'Settings::Queues:HoldMusic:ChooseFile' | abpLocalization }}
            </button>

            @if (holdMusicFileName) {
              <span class="queue-form__chip d-inline-flex align-items-center gap-2 px-2 py-1 small">
                <i class="pi pi-file"></i>
                <span class="queue-form__chip-label text-truncate">{{ holdMusicFileName }}</span>
                <button
                  type="button"
                  class="queue-form__chip-remove p-0"
                  [disabled]="isSubmitting"
                  (click)="removeHoldMusicFile()">
                  <i class="pi pi-times"></i>
                </button>
              </span>
            }
          </div>
        }
      </div>
    </div>

    @if (isAnnouncementsEnabled) {
      <hr class="queue-form__divider" />

      <!-- Announcements -->
      <div class="queue-form__section d-grid">
        <div class="pt-1">
          <h5 class="queue-form__section-title ps-2 fs-6 fw-semibold mb-1">{{ 'Settings::Queues:Announcements:Title' | abpLocalization }}</h5>
          <p class="queue-form__section-desc small mb-0">{{ 'Settings::Queues:Announcements:Subtitle' | abpLocalization }}</p>
        </div>
        <div class="queue-form__section-right d-flex flex-column gap-3">
          <!-- Announcement language: picks which recorded prompt set is played, not the wording. -->
          <div>
            <label class="queue-form__announce-language-label small d-block mb-1 fw-semibold text-uppercase">
              {{ 'Settings::Queues:Announcements:Language' | abpLocalization }}
            </label>
            <p-select
              formControlName="announcementLanguage"
              [options]="announcementLanguageOptions"
              optionValue="value"
              appendTo="body"
              styleClass="w-100">
              <ng-template let-opt pTemplate="selectedItem">{{ opt.label | abpLocalization }}</ng-template>
              <ng-template let-opt pTemplate="item">{{ opt.label | abpLocalization }}</ng-template>
            </p-select>
          </div>

          @if (isAnyAnnouncementEnabled) {
            <div class="queue-form__announce-nested px-3 py-3">
              <div class="d-flex align-items-center justify-content-between gap-3">
                <label class="form-label mb-0">{{ 'Settings::Queues:Announcements:Frequency:Label' | abpLocalization }}</label>
                <div class="d-flex align-items-center gap-2">
                  <input
                    type="number"
                    pInputText
                    formControlName="announcementFrequencySeconds"
                    [min]="announcementIntervalMin"
                    [max]="announcementIntervalMax"
                    class="queue-form__announce-field" />
                  <span class="queue-form__input-suffix small fw-medium text-muted">
                    {{ 'Settings::Queues:Announcements:Frequency:Unit' | abpLocalization }}
                  </span>
                </div>
              </div>
            </div>
          }

          <!-- Announce queue position -->
          <div>
            <div class="queue-form__announce-toggle d-flex align-items-center justify-content-between gap-3 px-3 py-3">
              <div>
                <div class="fw-semibold">{{ 'Settings::Queues:Announcements:Position:Label' | abpLocalization }}</div>
                <p class="queue-form__announce-hint small mt-1 mb-0">
                  {{ 'Settings::Queues:Announcements:Position:Hint' | abpLocalization }}
                </p>
              </div>
              <p-toggleSwitch formControlName="announceQueuePosition" />
            </div>

            @if (isAnnounceQueuePositionEnabled) {
              <div class="queue-form__announce-nested px-3 py-3">
                <div class="queue-form__announce-preview small d-flex align-items-start gap-2">
                  <i class="pi pi-volume-up"></i>
                  <span>&quot;{{ positionPreviewKey | abpLocalization }}&quot;</span>
                </div>
              </div>
            }
          </div>

          <!-- Announce estimated wait time -->
          <div>
            <div class="queue-form__announce-toggle d-flex align-items-center justify-content-between gap-3 px-3 py-3">
              <div>
                <div class="fw-semibold">{{ 'Settings::Queues:Announcements:WaitTime:Label' | abpLocalization }}</div>
                <p class="queue-form__announce-hint small mt-1 mb-0">
                  {{ 'Settings::Queues:Announcements:WaitTime:Hint' | abpLocalization }}
                </p>
              </div>
              <p-toggleSwitch formControlName="announceEstimatedWaitTime" />
            </div>

            @if (isAnnounceEstimatedWaitTimeEnabled) {
              <div class="queue-form__announce-nested px-3 py-3">
                <div class="d-flex align-items-center justify-content-between gap-3">
                  <label class="form-label mb-0">{{ 'Settings::Queues:Announcements:RoundTo:Label' | abpLocalization }}</label>
                  <div class="d-flex align-items-center gap-2">
                    <input
                      type="number"
                      pInputText
                      formControlName="estimatedWaitTimeRoundToSeconds"
                      [min]="announcementIntervalMin"
                      [max]="announcementIntervalMax"
                      class="queue-form__announce-field" />
                    <span class="queue-form__input-suffix small fw-medium text-muted">
                      {{ 'Settings::Queues:Announcements:Frequency:Unit' | abpLocalization }}
                    </span>
                  </div>
                </div>
                <div class="queue-form__announce-preview small d-flex align-items-start gap-2 mt-3">
                  <i class="pi pi-volume-up"></i>
                  <span>&quot;{{ waitTimePreviewKey | abpLocalization }}&quot;</span>
                </div>
              </div>
            }
          </div>
        </div>
      </div>
    }

  </form>

  <!-- Footer actions -->
  <cx-form-actions
    [cancelLabel]="'AbpUi::Cancel' | abpLocalization"
    [saveLabel]="'Settings::Queues:SaveQueue' | abpLocalization"
    cancelVariant="outline-secondary"
    saveType="button"
    [isSubmitting]="isSubmitting"
    (cancel)="onCancel()"
    (save)="onSave()">
  </cx-form-actions>
</div>
